import { z } from "zod";

import { createFindBatcher } from "./createFindBatcher";
import type { RemoteLookupProvider } from "./providers";
import type { FindFetcher, FindParams, FindResult } from "./types";

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new Error("Remote lookup find was aborted");
}

export function createFindFetcher<T extends string = string, Id = unknown, Item = unknown, Filters = unknown>(
  type: T,
  provider: RemoteLookupProvider<Id, Item, Filters>
): FindFetcher<Id, Item> {
  const paramsSchema = z.object({
    app_group_id: z.string(),
    id: provider.validators.id.nullable().optional(),
  });
  const resultSchema = z.object({
    item: provider.validators.item.nullable(),
  }) as z.ZodType<FindResult<Item>>; // Type assertion needed to satisfy non-strict typescript.
  const batchersByAppGroupId = new Map<string, ReturnType<typeof createFindBatcher<T, Id, Item>>>();
  return async (signal: AbortSignal, params: FindParams<Id>): Promise<FindResult<Item>> => {
    const parsedParams = paramsSchema.parse(params);

    // Short-circuit if no id is provided.
    if (parsedParams.id === null || parsedParams.id === undefined) {
      return { item: null };
    }

    let batcher = batchersByAppGroupId.get(parsedParams.app_group_id);
    if (!batcher) {
      batcher = createFindBatcher(parsedParams.app_group_id, type, provider);
      batchersByAppGroupId.set(parsedParams.app_group_id, batcher);
    }

    // An already-aborted lookup must not enqueue its id into a batch that
    // would still be requested for nothing. (Not signal.throwIfAborted():
    // older engines and jsdom lack it.)
    if (signal.aborted) {
      throw abortReason(signal);
    }

    // Create a promise that rejects if the signal is aborted. We can't thread
    // the signal through to actually cancel the batched request since it could
    // also be fetching other non-cancelled ids.
    const abortPromise = new Promise<never>((_resolve, reject) => {
      signal.addEventListener(
        "abort",
        () => {
          reject(abortReason(signal));
        },
        { once: true }
      );
    });

    const result = await Promise.race([abortPromise, batcher.fetch(parsedParams.id)]);
    return resultSchema.parse(result);
  };
}
